import React, { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router'
import UserContext from 'shared/components/context/user'
import LoginModal from 'shared/components/login-modal'
import { googleAnalyticsEvent, trackPageView } from 'shared/util/analytics'
import LegacyFileMap from './components/legacy-file'
import { useCreationAccess } from './hooks/use-creation-access'
import { useCreationProcessedFile } from './hooks/use-creation-processed-source'
import { getBasePathFromCreationTool, getCreationToolFromPath } from './routes'
import { CreationToolFrame, CreationToolSourceFile } from './types'
import CreationTool from './types/creation-tool'
import CreationToolOutputType from './types/output-type'
import CreationToolStep from './types/steps'
import CreationToolUserRestriction from './types/user-restriction'
import getCreationSteps from './utils/get-creation-steps'
import { removeTransparency } from './utils/image-data-utils'

export type CreationToolsContextProps = {
    tool: CreationTool
    source?: File | string
    sourceProcessed?: CreationToolSourceFile
    outputType: CreationToolOutputType
    steps: CreationToolStep[]
    stepFrames: CreationToolFrame[]
    currentStep: CreationToolStep | undefined
    userRestrictionAccess: CreationToolUserRestriction[]
    setFile: (source: File | string, outputType: CreationToolOutputType) => void
    saveFrames: (frames: CreationToolFrame[], step: CreationToolStep) => void
    trackEvent: (category: string, action: string, label: string, value?: string) => void
}

export const CreationToolsContext = createContext({} as CreationToolsContextProps)

type Props = {
    children: ReactNode
}

const CreationToolsContextManager = ({ children }: Props) => {
    const { user } = useContext(UserContext)
    const [source, setSource] = useState<File | string>()
    const [outputType, setOutputType] = useState<CreationToolOutputType>(CreationToolOutputType.Gif)
    const [steps, setSteps] = useState<CreationToolStep[]>([])
    const [showLoginModal, setShowLoginModal] = useState<boolean>(false)
    const stepFramesRef = useRef({})
    const userRestrictionAccess = useCreationAccess()
    const sourceProcessed = useCreationProcessedFile(source)
    const { pathname } = useLocation()
    const { push } = useHistory()
    const tool = getCreationToolFromPath(pathname)
    const basePath = getBasePathFromCreationTool(tool)
    const currentStep = steps.find(step => {
        const route = CreationToolStep.route(step)
        return pathname.indexOf(route) > -1
    })

    useEffect(() => {
        // TODO: Make sure sticker maker fires as stickermaker and not gifmaker?
        trackPageView(pathname)
    }, [pathname])

    const setFile = async (source: File | string, outputType: CreationToolOutputType) => {
        // if the tool requires login, show the login modal
        if (CreationTool.requiresLogin(tool) && !user.loggedIn) {
            setShowLoginModal(true)
            return
        }

        const steps = getCreationSteps(source, outputType)
        setSource(source)
        setOutputType(outputType)
        setSteps(steps)
        stepFramesRef.current = {}

        if (steps.length && outputType === CreationToolOutputType.Sticker) {
            push(`${basePath}${CreationToolStep.route(steps[0])}`)
        }
    }

    const saveFrames = (frames: CreationToolFrame[], step: CreationToolStep) => {
        if (!stepFramesRef.current) return

        if (step === CreationToolStep.Upload) {
            // if storing frames for upload step, remove transparent pixels
            // this makes it so there is no matte color showing in the output gif
            stepFramesRef.current[step] = frames.map(({ canvas, frameCount }) => {
                const context = canvas.getContext('2d')
                if (context) {
                    const imageData = context.getImageData(0, 0, canvas.width, canvas.height)
                    context.putImageData(removeTransparency(imageData), 0, 0)
                }
                return { canvas, frameCount }
            })
        } else {
            stepFramesRef.current[step] = frames
        }
    }

    const trackEvent = (category: string, action: string, label: string, value: string) => {
        // TODO: Add stricter typing to these function params if possible?
        googleAnalyticsEvent({ category, action, label, value })
    }

    return (
        <CreationToolsContext.Provider
            value={{
                tool,
                source,
                sourceProcessed,
                outputType,
                steps,
                stepFrames: currentStep ? stepFramesRef.current[currentStep] || [] : [],
                currentStep,
                userRestrictionAccess,
                setFile,
                saveFrames,
                trackEvent,
            }}
        >
            {children}
            {tool === CreationTool.GifMaker && <LegacyFileMap />}
            {showLoginModal && <LoginModal />}
        </CreationToolsContext.Provider>
    )
}

export default CreationToolsContextManager
